/**
 * 生成アレンジメントの可視化。ハードウェアの液晶ディスプレイ風の枠に
 * レイヤー別のノートバー・小節/拍グリッド・再生カーソルを SVG で描く
 * （表示専用）。メイン=シアン / SOUND A=ブルー / SOUND B=オレンジ。
 */

import React, { useMemo } from "react";
import styled from "styled-components";

import { midiNoteName } from "../utils/musicTheory";
import { type Arrangement } from "../utils/arrangement";
import {
  PPQ,
  TICKS_PER_BAR,
  type PhraseNote,
} from "../utils/phraseGenerator";
import { BLOOM_COLORS, PANEL, PANEL_FONT } from "../palette";

const VIEW_W = 960;
const VIEW_H = 220;
const PAD_X = 8;
const PAD_Y = 14;

const Display = styled.div`
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${PANEL.border};
  background: linear-gradient(180deg, #0a0f15 0%, ${PANEL.displayBg} 100%);
  box-shadow:
    inset 0 2px 10px rgba(0, 0, 0, 0.8),
    0 1px 0 rgba(255, 255, 255, 0.06);
  padding: 8px;
`;

const RollSvg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`;

const NoteLabel = styled.text`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 9px;
  letter-spacing: 0.08em;
  fill: rgba(223, 231, 238, 0.55);
`;

const LAYER_COLORS = {
  main: BLOOM_COLORS.cyan,
  soundA: BLOOM_COLORS.blue,
  soundB: BLOOM_COLORS.orange,
} as const;

export interface PianoRollProps {
  arrangement: Arrangement;
  /** 再生カーソル位置（tick）。null で非表示 */
  playheadTick: number | null;
}

const PianoRoll: React.FC<PianoRollProps> = ({ arrangement, playheadTick }) => {
  const { main, soundA, soundB, lengthTicks } = arrangement;

  const geometry = useMemo(() => {
    const totalTicks = Math.max(lengthTicks, TICKS_PER_BAR);
    // 音高レンジは全レイヤー（メイン + A + B）から求める
    const melodic = [...main, ...soundA, ...soundB];
    let minNote = 127;
    let maxNote = 0;
    for (const n of melodic) {
      minNote = Math.min(minNote, n.midiNote);
      maxNote = Math.max(maxNote, n.midiNote);
    }
    if (melodic.length === 0) {
      minNote = 60;
      maxNote = 72;
    }
    // 上下 2 半音の余白を取りつつ最低 1 オクターブは確保する
    minNote -= 2;
    maxNote += 2;
    if (maxNote - minNote < 12) {
      const pad = Math.ceil((12 - (maxNote - minNote)) / 2);
      minNote -= pad;
      maxNote += pad;
    }

    const innerW = VIEW_W - PAD_X * 2;
    const innerH = VIEW_H - PAD_Y * 2;
    const noteSpan = maxNote - minNote;
    const rowH = innerH / (noteSpan + 1);

    const x = (tick: number) => PAD_X + (tick / totalTicks) * innerW;
    const y = (midi: number) => PAD_Y + (maxNote - midi) * rowH;

    return { totalTicks, minNote, maxNote, rowH, x, y };
  }, [main, soundA, soundB, lengthTicks]);

  const { totalTicks, minNote, maxNote, rowH, x, y } = geometry;

  const beatLines = useMemo(() => {
    const lines: { tick: number; isBar: boolean }[] = [];
    for (let t = 0; t <= totalTicks; t += PPQ) {
      lines.push({ tick: t, isBar: t % TICKS_PER_BAR === 0 });
    }
    return lines;
  }, [totalTicks]);

  // C の行にだけ薄くガイド線 + 音名を出す
  const cRows = useMemo(() => {
    const rows: number[] = [];
    for (let m = minNote; m <= maxNote; m++) {
      if (((m % 12) + 12) % 12 === 0) rows.push(m);
    }
    return rows;
  }, [minNote, maxNote]);

  const barHeight = Math.max(3, rowH * 0.8);

  const renderBars = (notes: PhraseNote[], color: string, opacityScale = 1) =>
    notes.map((n, i) => (
      <rect
        key={i}
        x={x(n.tick)}
        y={y(n.midiNote) + (rowH - barHeight) / 2}
        width={Math.max(2, x(n.tick + n.durationTicks) - x(n.tick) - 1)}
        height={barHeight}
        rx={1.5}
        fill={color}
        opacity={(0.45 + (n.velocity / 127) * 0.55) * opacityScale}
      />
    ));

  return (
    <Display>
      <RollSvg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        aria-label="生成アレンジメントのピアノロール"
        role="img"
      >
        {/* C 行ガイド */}
        {cRows.map((m) => (
          <g key={`c-${m}`}>
            <line
              x1={PAD_X}
              x2={VIEW_W - PAD_X}
              y1={y(m) + rowH / 2}
              y2={y(m) + rowH / 2}
              stroke="rgba(223,231,238,0.09)"
              strokeWidth="1"
            />
            <NoteLabel x={PAD_X + 2} y={y(m) + rowH / 2 - 3}>
              {midiNoteName(m)}
            </NoteLabel>
          </g>
        ))}

        {/* 拍 / 小節グリッド */}
        {beatLines.map(({ tick, isBar }) => (
          <line
            key={`grid-${tick}`}
            x1={x(tick)}
            x2={x(tick)}
            y1={PAD_Y - 6}
            y2={VIEW_H - PAD_Y + 6}
            stroke={isBar ? "rgba(223,231,238,0.3)" : "rgba(223,231,238,0.12)"}
            strokeWidth={isBar ? 1.4 : 1}
          />
        ))}

        {/* ノートバー（ベロシティを不透明度に反映）。メインを最前面に */}
        <g>{renderBars(soundA, LAYER_COLORS.soundA, 0.75)}</g>
        <g>{renderBars(soundB, LAYER_COLORS.soundB, 0.85)}</g>
        <g>{renderBars(main, LAYER_COLORS.main)}</g>

        {/* 再生カーソル */}
        {playheadTick !== null && playheadTick <= totalTicks && (
          <line
            x1={x(playheadTick)}
            x2={x(playheadTick)}
            y1={PAD_Y - 8}
            y2={VIEW_H - PAD_Y + 8}
            stroke={BLOOM_COLORS.yellow}
            strokeWidth="2"
          />
        )}
      </RollSvg>
    </Display>
  );
};

export default PianoRoll;
